import type { PostHog } from "posthog-js";

import type { PublicListCta, PublicPageSource } from "@soonlist/validators";
import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPS,
  PUBLIC_PAGE_SOURCE,
} from "@soonlist/validators";

const KNOWN_TOP_LEVEL_ROUTES = new Set([
  "2024",
  "about",
  "account",
  "admin",
  "api",
  "blog",
  "contact",
  "delete-account",
  "event",
  "explore",
  "get-started",
  "help",
  "install",
  "join",
  "list",
  "lists",
  "mcp",
  "new",
  "privacy",
  "sign-in",
  "sign-up",
  "terms",
  "terms-ios",
]);

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function getPublicPageContext(pathname: string): {
  source: PublicPageSource;
  creatorUsername?: string;
} {
  const segments = pathname.split("/").filter(Boolean);
  const [first, second] = segments;

  if (first === "event") {
    return { source: PUBLIC_PAGE_SOURCE.EVENT_PAGE };
  }
  if (first === "list") {
    return { source: PUBLIC_PAGE_SOURCE.NAMED_LIST_PAGE };
  }

  const isCreatorPath =
    segments.length === 1 || (segments.length === 2 && second === "upcoming");
  if (first && isCreatorPath && !KNOWN_TOP_LEVEL_ROUTES.has(first)) {
    return {
      source: PUBLIC_PAGE_SOURCE.LIST_PAGE,
      creatorUsername: decodeSegment(first),
    };
  }

  return { source: PUBLIC_PAGE_SOURCE.MARKETING_PAGE };
}

export function capturePublicPageCta(
  posthog: PostHog,
  opts: {
    cta: PublicListCta;
    pathname: string;
    creatorUsername?: string;
    eventId?: string;
    sendBeacon?: boolean;
  },
): void {
  const context = getPublicPageContext(opts.pathname);
  const creatorUsername = opts.creatorUsername ?? context.creatorUsername;

  posthog.capture(
    ANALYTICS_EVENTS.PUBLIC_LIST_CTA_CLICKED,
    {
      [ANALYTICS_PROPS.CTA]: opts.cta,
      [ANALYTICS_PROPS.SOURCE]: context.source,
      ...(creatorUsername
        ? { [ANALYTICS_PROPS.CREATOR_USERNAME]: creatorUsername }
        : {}),
      ...(opts.eventId ? { [ANALYTICS_PROPS.EVENT_ID]: opts.eventId } : {}),
    },
    opts.sendBeacon ? { transport: "sendBeacon" } : undefined,
  );
}
